import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Chip,
  Switch,
  FormControlLabel,
  List,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import i18n from 'i18n-js';

import PLANS from '../../constants/plans';
import PRICING from '../../constants/pricing';
import { AnalyticsProvider, FetchProvider } from '../../providers';
import { useLoginState } from '../Menu/components/LoginDialog/context';
import {
  Container,
  PricingHeader,
  PlansContainer,
  PlanCard,
  PlanHeader,
  PlanPrice,
  PlanFeatures,
  FeatureItem,
  PlanButton,
  PopularBadge,
  ToggleContainer,
} from './styles';

export default function PricingTable() {
  const [isYearly, setIsYearly] = useState(true);
  const user = useSelector((state) => state.user);
  const { countryCode, id: userId } = user.data;
  const { openLoginDialog } = useLoginState();

  const prices = PRICING[countryCode] || PRICING['US'];

  const handlePlanSelect = async (planId) => {
    const period = isYearly ? 'yearly' : 'monthly';

    try {
      if (!userId) {
        openLoginDialog();
        return;
      }

      // Get currency code from pricing constants
      const samplePrice = prices.basic.monthly;
      const currencySymbol = samplePrice.replace(/[\d.,\s]/g, '');
      const currency = currencySymbol === '€' ? 'eur' : 'usd';

      const result = await FetchProvider.fetchPost(
        'stripe/create-checkout-session',
        {
          countryCode,
          plan: planId,
          period,
          currency,
        }
      );

      let stripe = Stripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY); // eslint-disable-line
      stripe.redirectToCheckout({ sessionId: result.sessionId });

      AnalyticsProvider.event('CLICK_PLAN', { planId, period, currency });
    } catch (e) {
      console.error('Error creating checkout session:', e);
    }
  };

  const renderFeature = (featureKey, included = true) => (
    <FeatureItem key={featureKey} included={included}>
      <ListItemIcon sx={{ minWidth: 32 }}>
        {included ? (
          <CheckIcon color="success" fontSize="small" />
        ) : (
          <CloseIcon color="disabled" fontSize="small" />
        )}
      </ListItemIcon>
      <ListItemText
        primary={i18n.t(`pricing.features.${featureKey}`)}
        primaryTypographyProps={{
          variant: 'body2',
          color: included ? 'text.primary' : 'text.disabled',
        }}
      />
    </FeatureItem>
  );

  const calculateMonthlySavings = (planId) => {
    // Extract numeric values from pricing strings
    const monthlyStr = prices[planId].monthly;
    const yearlyStr = prices[planId].yearly;

    const monthlyNum = parseFloat(monthlyStr.replace(/[^\d.]/g, ''));
    const yearlyNum = parseFloat(yearlyStr.replace(/[^\d.]/g, ''));

    const monthlyCost = monthlyNum * 12;
    const savings = monthlyCost - yearlyNum;
    const percentage = Math.round((savings / monthlyCost) * 100);
    return { savings, percentage };
  };

  const getMonthlyCost = (planId) => {
    if (isYearly) {
      const yearlyNum = parseFloat(
        prices[planId].yearly.replace(/[^\d.]/g, '')
      );
      return Math.round(yearlyNum / 12);
    } else {
      return parseFloat(prices[planId].monthly.replace(/[^\d.]/g, ''));
    }
  };

  const getCurrency = () => {
    const samplePrice = prices.basic.monthly;
    return samplePrice.replace(/[\d.,\s]/g, '');
  };

  const formatPrice = (amount, currency) => {
    // Euro goes after the number in Spanish/European countries
    if (currency === '€') {
      return `${amount}€`;
    }
    // Dollar goes before the number
    return `${currency}${amount}`;
  };

  return (
    <Container>
      <PricingHeader>
        <Typography variant="h3" component="h1" align="center" gutterBottom>
          {i18n.t('pricing.title')}
        </Typography>
        <Typography
          variant="h6"
          align="center"
          color="text.secondary"
          paragraph
        >
          {i18n.t('pricing.subtitle')}
        </Typography>

        <ToggleContainer>
          <FormControlLabel
            control={
              <Switch
                checked={isYearly}
                onChange={(e) => setIsYearly(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1">
                  {i18n.t('pricing.yearlyBilling')}
                </Typography>
                <Chip
                  label={i18n.t('pricing.saveUp')}
                  color="primary"
                  size="small"
                  variant="filled"
                />
              </Box>
            }
          />
        </ToggleContainer>
      </PricingHeader>

      <PlansContainer>
        {PLANS.map((plan) => {
          const monthlyPrice = getMonthlyCost(plan.id);
          const savings = isYearly ? calculateMonthlySavings(plan.id) : null;
          const currency = getCurrency();

          return (
            <PlanCard key={plan.id} popular={plan.popular}>
              {plan.popular && (
                <PopularBadge>
                  <StarIcon fontSize="small" />
                  {i18n.t('pricing.mostPopular')}
                </PopularBadge>
              )}

              <PlanHeader>
                <Typography variant="h5" component="h3" fontWeight="bold">
                  {i18n.t(`pricing.plans.${plan.id}.name`)}
                </Typography>

                <PlanPrice>
                  <Typography variant="h3" component="div" fontWeight="bold">
                    {formatPrice(monthlyPrice, currency)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {i18n.t('pricing.perMonth')}
                  </Typography>
                  {isYearly && (
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{ mt: 0.5 }}
                    >
                      {i18n.t('pricing.billedYearly', {
                        amount: prices[plan.id].yearly,
                      })}
                    </Typography>
                  )}
                  {isYearly && savings && (
                    <Chip
                      label={i18n.t('pricing.save', {
                        percentage: savings.percentage,
                      })}
                      color="success"
                      size="small"
                      variant="outlined"
                      sx={{ mt: 1 }}
                    />
                  )}
                </PlanPrice>
              </PlanHeader>

              <Divider sx={{ my: 2 }} />

              <PlanFeatures>
                <List disablePadding>
                  {plan.features.included.map((feature) =>
                    renderFeature(feature, true)
                  )}
                  {plan.features.excluded.map((feature) =>
                    renderFeature(feature, false)
                  )}
                </List>
              </PlanFeatures>

              <PlanButton>
                <Button
                  variant={plan.popular ? 'contained' : 'outlined'}
                  color="primary"
                  fullWidth
                  size="large"
                  onClick={() => handlePlanSelect(plan.id)}
                  sx={{ py: 1.5 }}
                >
                  {i18n.t('pricing.selectPlan')}
                </Button>
              </PlanButton>
            </PlanCard>
          );
        })}
      </PlansContainer>
    </Container>
  );
}
